"use client";

import { useState, useRef, useEffect } from "react";


// Maximum visible items before scrolling is needed
const MAX_VISIBLE_ITEMS = 4;

interface MobileThemePickerProps {
    themes: { id: string; name: string; iconUrl?: string | null }[];
    selectedThemeId: string;
    onSelectTheme: (id: string) => void;
}

export const MobileThemePicker = ({
    themes,
    selectedThemeId,
    onSelectTheme,
}: MobileThemePickerProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollUp, setCanScrollUp] = useState(false);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const needsScroll = themes.length > MAX_VISIBLE_ITEMS;

    const checkScrollPosition = () => {
        const container = scrollContainerRef.current;
        if (!container) return;
        setCanScrollUp(container.scrollTop > 5);
        setCanScrollDown(
            container.scrollTop + container.clientHeight < container.scrollHeight - 5
        );
    };

    useEffect(() => {
        if (isOpen && needsScroll) {
            // Small delay to let the panel render
            setTimeout(checkScrollPosition, 50);
        }
    }, [isOpen, needsScroll]);

    const scrollBy = (direction: "up" | "down") => {
        const container = scrollContainerRef.current;
        if (!container) return;
        const amount = direction === "up" ? -70 : 70;
        container.scrollBy({ top: amount, behavior: "smooth" });
    };

    return (
        <div className="md:hidden">
            {/* Backdrop */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-[49]"
                    onClick={() => setIsOpen(false)}
                />
            )}

            {/* Panel + FAB container */}
            <div className="fixed bottom-24 left-4 z-50 flex flex-col items-center">
                {/* Theme dropdown panel */}
                <div
                    className={`
                        absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                        flex flex-col items-center
                        rounded-[1.8rem]
                        shadow-[0_8px_32px_rgba(0,0,0,0.25)]
                        overflow-hidden
                        transition-all duration-300 ease-out origin-bottom
                        ${isOpen
                            ? "opacity-100 scale-100 translate-y-0"
                            : "opacity-0 scale-75 translate-y-8 pointer-events-none"
                        }
                    `}
                    style={{
                        background: "linear-gradient(180deg, color-mix(in srgb, var(--primary-color) 40%, white 60%) 0%, color-mix(in srgb, var(--primary-color) 55%, white 45%) 50%, color-mix(in srgb, var(--primary-color) 65%, white 35%) 100%)",
                        minWidth: "4.2rem",
                    }}
                >
                    {/* Up arrow — always visible */}
                    <button
                        onClick={() => scrollBy("up")}
                        className={`
                            flex items-center justify-center w-full py-1
                            transition-opacity duration-200
                            ${canScrollUp ? "opacity-100" : "opacity-30 pointer-events-none"}
                        `}
                        aria-label="Scroll themes up"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="h-4 w-4"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="18 15 12 9 6 15" />
                        </svg>
                    </button>

                    {/* Scrollable theme list */}
                    <div
                        ref={scrollContainerRef}
                        onScroll={checkScrollPosition}
                        className="flex flex-col items-center gap-0.5 px-1.5 py-1"
                        style={{
                            maxHeight: needsScroll ? `${MAX_VISIBLE_ITEMS * 70}px` : "none",
                            overflowY: needsScroll ? "auto" : "visible",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        <style>{`
                            .theme-scroll-container::-webkit-scrollbar {
                                display: none;
                            }
                        `}</style>
                        {themes.map((theme) => {
                            const isSelected = selectedThemeId === theme.id;
                            return (
                                <button
                                    key={theme.id}
                                    onClick={() => {
                                        onSelectTheme(theme.id);
                                        setIsOpen(false);
                                    }}
                                    className={`
                                        flex flex-col items-center gap-0.5 rounded-xl px-2 py-1.5
                                        transition-all duration-200 min-w-[3.5rem]
                                        ${isSelected
                                            ? "scale-105"
                                            : "hover:bg-white/10 active:scale-95"
                                        }
                                    `}
                                >
                                    <div className={`
                                        flex items-center justify-center rounded-full w-9 h-9 bg-white/20
                                        ${isSelected ? "ring-2 ring-white ring-offset-1" : ""}
                                    `}>
                                        {theme.iconUrl && (
                                            <img src={theme.iconUrl} alt={theme.name} className="h-6 w-6 object-contain rounded-full" />
                                        )}
                                    </div>
                                    <span className={`
                                        text-[0.5rem] font-semibold leading-tight text-center max-w-[3.5rem]
                                        ${isSelected ? "text-white" : "text-white/90"}
                                    `}>
                                        {theme.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Down arrow — always visible */}
                    <button
                        onClick={() => scrollBy("down")}
                        className={`
                            flex items-center justify-center w-full py-1
                            transition-opacity duration-200
                            ${canScrollDown ? "opacity-100" : "opacity-30 pointer-events-none"}
                        `}
                        aria-label="Scroll themes down"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="h-4 w-4"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <polyline points="6 9 12 15 18 9" />
                        </svg>
                    </button>
                </div>

                {/* Paint palette FAB button */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`
                        relative flex items-center justify-center
                        h-14 w-14 rounded-full
                        shadow-[0_4px_24px_rgba(0,0,0,0.25)]
                        transition-transform duration-300
                        ${isOpen ? "scale-90" : "scale-100"}
                    `}
                    aria-label="Toggle theme picker"
                    style={{
                        background: "linear-gradient(135deg, color-mix(in srgb, var(--primary-color) 70%, white 30%) 0%, var(--primary-color) 50%, color-mix(in srgb, var(--primary-color) 85%, black 15%) 100%)",
                    }}
                >
                    {/* Paint palette icon */}
                    <svg
                        viewBox="0 0 40 40"
                        fill="none"
                        className="h-8 w-8"
                    >
                        {/* Palette body */}
                        <path
                            d="M20 4C11.2 4 4 11.2 4 20s7.2 16 16 16c1.6 0 2.8-1.2 2.8-2.8 0-.7-.3-1.3-.7-1.8-.4-.5-.6-1.1-.6-1.8 0-1.6 1.2-2.8 2.8-2.8H27c4.8 0 8.8-4 8.8-8.8C35.8 11 28.8 4 20 4z"
                            fill="white"
                            stroke="white"
                            strokeWidth="0.5"
                        />
                        {/* Color dots */}
                        <circle cx="13" cy="16" r="2.5" fill="#EF4444" />
                        <circle cx="19" cy="11" r="2.5" fill="#3B82F6" />
                        <circle cx="26" cy="14" r="2.5" fill="#22C55E" />
                        <circle cx="14" cy="24" r="2.5" fill="#F59E0B" />
                    </svg>
                </button>
            </div>
        </div>
    );
};
